// Site Inductions dashboard — Safety & QA command centre (Phase 1).
import { useEffect, useState } from "react";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, HardHat, Search, Loader2, Eye, AlertTriangle, Users, CalendarCheck, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_BADGE = {
  ACTIVE: "bg-emerald-100 text-emerald-700 border-emerald-200",
  DRAFT: "bg-amber-100 text-amber-700 border-amber-200",
  SUPERSEDED: "bg-zinc-100 text-zinc-500 border-zinc-200",
};
const fmt = (d) => { try { return new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };

export default function SiteInductions() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/site-inductions", { params: { q: q || undefined, status: filter } }); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q]);

  const m = data?.metrics || {};
  const rows = data?.inductions || [];

  return (
    <div className="pb-10" data-testid="site-inductions-page">
      <PageHeader eyebrow="// Site Access & Safety Briefings" title="Site Inductions"
        subtitle="Create, deliver and track site inductions for workers, subcontractors and visitors."
        action={data?.can_manage && (
          <Button onClick={() => setAddOpen(true)} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[11px] h-9" data-testid="si-new">
            <Plus className="w-4 h-4 mr-1.5" /> New site induction
          </Button>
        )} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="si-metrics">
        <Kpi icon={Building2} value={m.active_sites ?? 0} label="Active sites" />
        <Kpi icon={Users} value={m.inducted_people ?? 0} label="People inducted" />
        <Kpi icon={CalendarCheck} value={m.completed_this_month ?? 0} label="This month" />
        <Kpi icon={AlertTriangle} value={m.needs_attention ?? 0} label="Need attention" tone={(m.needs_attention || 0) > 0 ? "amber" : "neutral"} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs, sites, inductions…" className="pl-9 rounded-lg h-9" data-testid="si-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] h-9 rounded-lg" data-testid="si-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="needs_attention">Needs attention</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500" data-testid="si-empty">
          No site inductions yet. Create one from a job to get started.
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto" data-testid="si-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="py-2.5 px-4">Site / Job</th><th className="px-3">Revision</th><th className="px-3">Status</th>
              <th className="px-3 text-right">Inducted</th><th className="px-3">Attention</th><th className="px-3">Updated</th><th className="px-3" />
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const st = r.current_revision?.status || "DRAFT";
                return (
                  <tr key={r.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`si-row-${r.id}`}>
                    <td className="py-2.5 px-4"><div className="font-bold">{r.site_name}</div><div className="text-[11px] text-zinc-400">{r.job_number}</div></td>
                    <td className="px-3 font-mono">REV {String(r.current_revision?.revision_number || 1).padStart(2, "0")}</td>
                    <td className="px-3"><span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${STATUS_BADGE[st]}`}>{st}</span></td>
                    <td className="px-3 text-right tabular-nums">{r.inducted_count || 0}</td>
                    <td className="px-3">{(r.attention || []).length ? <span className="text-amber-600 text-xs font-bold">{r.attention[0]}</span> : <span className="text-zinc-300">—</span>}</td>
                    <td className="px-3 text-zinc-500">{fmt(r.updated_at)}</td>
                    <td className="px-3 text-right"><Button size="sm" variant="outline" onClick={() => nav(`/app/safety/site-inductions/${r.id}`)} className="h-8 rounded-lg text-[11px] font-bold" data-testid={`si-view-${r.id}`}><Eye className="w-3.5 h-3.5 mr-1.5" /> View</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && <NewInductionDialog onClose={() => setAddOpen(false)} onCreated={(id) => nav(`/app/safety/site-inductions/${id}`)} />}
    </div>
  );
}

const toneCls = { amber: "border-amber-200 bg-amber-50 text-amber-700", neutral: "border-zinc-200 bg-white text-zinc-900" };
const Kpi = ({ icon: Icon, value, label, tone = "neutral" }) => (
  <div data-tone={tone} className={`hs-kpi rounded-xl border p-3.5 ${toneCls[tone]}`}>
    <div className="flex items-center justify-between"><span className="hs-kpi-value text-2xl font-extrabold tabular-nums">{value}</span><Icon className="w-4 h-4 opacity-60" /></div>
    <div className="hs-kpi-label text-[10px] font-bold uppercase tracking-widest opacity-80 mt-0.5">{label}</div>
  </div>
);

function NewInductionDialog({ onClose, onCreated }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/jobs").then((r) => { const d = r.data; setJobs(Array.isArray(d) ? d : d.jobs || d.items || []); }).catch(() => {});
  }, []);
  const create = async () => {
    if (!jobId) return toast.error("Pick a job");
    setBusy(true);
    try { const { data } = await api.post("/site-inductions", { job_id: jobId }); onCreated(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create"); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl max-w-md" data-testid="si-new-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-black uppercase tracking-tight"><HardHat className="w-5 h-5 text-[#FF5F15]" /> New site induction</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-500">Pick a job to auto-fill the basics. One active induction per job — use revisions for changes.</p>
        <div className="mt-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Project / Job</label>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger data-testid="si-job-select"><SelectValue placeholder="Select a job…" /></SelectTrigger>
            <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} — ` : ""}{j.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={create} disabled={busy} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="si-create">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create induction →"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
